import { useMemo, useState } from "preact/hooks"
import useSWR from "swr"
import { Pencil, Trash2 } from "lucide-react"
import { SettingsLayout } from "../layout"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { toast } from "@/components/ui/use-toast"
import { usePageTitle } from "@/utils"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { CategoryRule, ProposedChange, delete_category_rule, fetch_category_rules } from "./api"
import { BuiltinRulesSection } from "./builtin_rules"
import { pattern_to_english, regex_to_pattern } from "./pattern"
import { PreviewDialog } from "./preview_dialog"
import { RenameMergeDialog } from "./rename_merge_dialog"
import { RuleDialog, source_label } from "./rule_dialog"

interface NameGroup {
  name: string
  rules: CategoryRule[]
  matched_count: number
  manual_count: number
}

/** Group rules by category name (case-insensitive), most-used names first. */
export function group_rules_by_name(rules: CategoryRule[]): NameGroup[] {
  const groups = new Map<string, NameGroup>()
  for (const r of rules) {
    const key = r.name.toLowerCase()
    const existing = groups.get(key)
    if (existing) {
      existing.rules.push(r)
      existing.matched_count += r.matched_count
      existing.manual_count += r.manual_count
    } else {
      groups.set(key, { name: r.name, rules: [r], matched_count: r.matched_count, manual_count: r.manual_count })
    }
  }
  return [...groups.values()].sort((a, b) => b.matched_count + b.manual_count - (a.matched_count + a.manual_count) || a.name.localeCompare(b.name))
}

export function SettingsCategoriesPage() {
  usePageTitle("Categories")
  const { data: rules, mutate } = useSWR("category-rules", () => fetch_category_rules())
  const [source_filter, setSourceFilter] = useState<string>("all")
  const [type_filter, setTypeFilter] = useState<string>("all")

  // dialog state: a pending change flows rule/rename dialog → preview dialog → apply
  const [pending, setPending] = useState<{ change: ProposedChange; title: string } | null>(null)
  const [editing_rule, setEditingRule] = useState<CategoryRule | null>(null)
  const [rule_dialog_open, setRuleDialogOpen] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)

  const sources = useMemo(() => [...new Set((rules ?? []).map((r) => r.source))].sort(), [rules])
  const category_names = useMemo(() => [...new Set((rules ?? []).map((r) => r.name))].sort(), [rules])
  const filtered = useMemo(
    () =>
      (rules ?? [])
        .filter((r) => source_filter === "all" || r.source === source_filter)
        .filter((r) => matches_type_filter(r, type_filter)),
    [rules, source_filter, type_filter],
  )
  const groups = useMemo(() => group_rules_by_name(filtered), [filtered])
  const example_invoice_numbers = useMemo(
    () => (rules ?? []).filter((r) => source_filter === "all" || r.source === source_filter).flatMap((r) => r.example_invoice_numbers),
    [rules, source_filter],
  )

  function open_preview(change: ProposedChange, title: string) {
    setRuleDialogOpen(false)
    setRenaming(null)
    setPending({ change, title })
  }

  function toggle_disabled(rule: CategoryRule) {
    const change: ProposedChange = rule.disabled ? { kind: "edit_rule", id: rule.id, disabled: false } : { kind: "disable_rule", id: rule.id }
    open_preview(change, `${rule.disabled ? "Enable" : "Disable"} rule: ${rule.name} — ${rule.description}`)
  }

  async function remove_rule(rule: CategoryRule) {
    const res = await delete_category_rule(rule.id)
    if (res.ok) {
      toast({ title: "Rule deleted" })
      void mutate()
    } else {
      toast({ title: "Could not delete rule", description: res.error.message, variant: "destructive" })
    }
  }

  return (
    <SettingsLayout
      action={
        <Button
          class="text-sm"
          onClick={() => {
            setEditingRule(null)
            setRuleDialogOpen(true)
          }}>
          Add rule
        </Button>
      }>
      <div class="bg-white rounded-lg shadow-sm p-6 space-y-6">
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 class="text-lg font-semibold leading-7 text-gray-900">Category rules</h2>
            <p class="text-sm text-gray-500 max-w-2xl">
              How deductions are automatically categorized on import. <span class="font-medium">Standard</span> rules are shared across all
              brands; editing one creates a customized copy for your organization. Every change shows the affected deductions before it's
              applied.
            </p>
          </div>
          <div class="flex gap-2">
            <div class="w-44">
              <Select value={type_filter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="modified">Modified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div class="w-48">
              <Select value={source_filter} onValueChange={setSourceFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All distributors</SelectItem>
                  {sources.map((s) => (
                    <SelectItem key={s} value={s}>
                      {source_label(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {!rules ? (
          <div class="text-sm text-gray-500">Loading…</div>
        ) : groups.length === 0 ? (
          <div class="text-sm text-gray-500">No category rules yet. Add one to start auto-categorizing deductions.</div>
        ) : (
          <div class="space-y-8">
            {groups.map((group) => (
              <NameGroupTable key={group.name.toLowerCase()} group={group} show_source={source_filter === "all"} onEdit={(r) => {
                setEditingRule(r)
                setRuleDialogOpen(true)
              }} onToggleDisabled={toggle_disabled} onRename={() => setRenaming(group.name)} onDelete={remove_rule} />
            ))}
          </div>
        )}
      </div>

      <BuiltinRulesSection />

      {rule_dialog_open && (
        <RuleDialog
          open={rule_dialog_open}
          rule={editing_rule}
          default_source={source_filter === "all" ? (sources[0] ?? "unfi") : source_filter}
          sources={sources.length > 0 ? sources : ["unfi", "kehe"]}
          category_names={category_names}
          example_invoice_numbers={editing_rule ? example_invoice_numbers : example_invoice_numbers}
          onClose={() => setRuleDialogOpen(false)}
          onPreview={open_preview}
        />
      )}
      <RenameMergeDialog from_name={renaming} category_names={category_names} onClose={() => setRenaming(null)} onPreview={open_preview} />
      <PreviewDialog
        change={pending?.change ?? null}
        title={pending?.title ?? ""}
        onClose={() => setPending(null)}
        onApplied={() => void mutate()}
      />
    </SettingsLayout>
  )
}

interface NameGroupTableProps {
  group: NameGroup
  show_source: boolean
  onEdit: (rule: CategoryRule) => void
  onToggleDisabled: (rule: CategoryRule) => void
  onRename: () => void
  onDelete: (rule: CategoryRule) => void
}

function NameGroupTable({ group, show_source, onEdit, onToggleDisabled, onRename, onDelete }: NameGroupTableProps) {
  const total = group.matched_count + group.manual_count
  return (
    <div>
      <div class="flex items-baseline gap-2 mb-2">
        <h3 class="text-base font-semibold text-gray-900">{group.name}</h3>
        <span class="text-xs text-gray-500">
          {total > 0 ? `${total} deduction${total === 1 ? "" : "s"}` : "no deductions"}
        </span>
        <button type="button" class="text-xs text-blue-600 hover:underline cursor-pointer" onClick={onRename}>
          Rename / merge
        </button>
      </div>
      <div class="overflow-x-auto border rounded-md">
        <table class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="bg-gray-50">
            <tr>
              <Th>Description</Th>
              {show_source && <Th>Distributor</Th>}
              <Th>Pattern</Th>
              <Th>Type</Th>
              <Th>Matched</Th>
              <Th>Enabled</Th>
              <th class="px-3 py-2" />
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            {group.rules.map((rule) => (
              <RuleRow key={rule.id} rule={rule} show_source={show_source} onEdit={onEdit} onToggleDisabled={onToggleDisabled} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Th({ children }: { children: preact.ComponentChildren }) {
  return <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{children}</th>
}

export function matches_type_filter(rule: CategoryRule, type_filter: string): boolean {
  switch (type_filter) {
    case "standard":
      return rule.is_global
    case "custom":
      return !rule.is_global
    case "modified":
      return rule.replaces_category_id !== null
    default:
      return true
  }
}

/** "Invoice # starts with …" / "Description contains …" summary of a rule's patterns. */
export function rule_pattern_summary(rule: CategoryRule): string {
  const parts: string[] = []
  if (rule.invoice_regex) {
    parts.push(`Invoice # ${pattern_to_english(regex_to_pattern(rule.invoice_regex))}`)
  }
  if (rule.remark_regex) {
    parts.push(`Description ${pattern_to_english(regex_to_pattern(rule.remark_regex))}`)
  }
  return parts.join(rule.source === "amazon" ? " and " : ", or ")
}

function RuleBadge({ rule }: { rule: CategoryRule }) {
  if (rule.is_global) {
    return <span class="text-xs rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">Standard</span>
  }
  if (rule.replaces_category_id) {
    return <span class="text-xs rounded-full bg-purple-50 px-2 py-0.5 text-purple-700">Modified</span>
  }
  return <span class="text-xs rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">Custom</span>
}

interface RuleRowProps {
  rule: CategoryRule
  show_source: boolean
  onEdit: (rule: CategoryRule) => void
  onToggleDisabled: (rule: CategoryRule) => void
  onDelete: (rule: CategoryRule) => void
}

function RuleRow({ rule, show_source, onEdit, onToggleDisabled, onDelete }: RuleRowProps) {
  const pattern = rule_pattern_summary(rule)
  const examples = rule.example_invoice_numbers.slice(0, 3).join(", ")
  return (
    <tr class={rule.disabled ? "opacity-50" : ""}>
      <td class="px-3 py-2 align-top max-w-[16rem]">
        {rule.description || <Dash />}
        {rule.duplicate_count > 1 && (
          <span
            class="ml-1.5 text-xs rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-700 whitespace-nowrap"
            title={`${rule.duplicate_count} identical duplicate rows exist for this category; they are shown as one entry with combined counts.`}>
            ×{rule.duplicate_count}
          </span>
        )}
      </td>
      {show_source && <td class="px-3 py-2 align-top whitespace-nowrap">{source_label(rule.source)}</td>}
      <td class="px-3 py-2 align-top max-w-[24rem]">
        {pattern ? (
          <span class="line-clamp-2 break-all" title={pattern}>
            {pattern}
          </span>
        ) : (
          <span class="text-gray-400">No pattern — assigned manually or by the wizard</span>
        )}
        {examples && (
          <div class="text-xs text-gray-400 font-mono mt-0.5 truncate max-w-[24rem]" title="Recent matching invoice numbers">
            e.g. {examples}
          </div>
        )}
      </td>
      <td class="px-3 py-2 align-top">
        <RuleBadge rule={rule} />
      </td>
      <td class="px-3 py-2 align-top whitespace-nowrap tabular-nums">
        {rule.matched_count > 0 || rule.manual_count > 0 ? (
          <span title={`${rule.matched_count} auto-matched, ${rule.manual_count} manually assigned`}>
            {rule.matched_count + rule.manual_count}
          </span>
        ) : (
          <Dash />
        )}
      </td>
      <td class="px-3 py-2 align-middle">
        <Switch checked={!rule.disabled} onCheckedChange={() => onToggleDisabled(rule)} aria-label="Rule enabled" />
      </td>
      <td class="px-3 py-2 text-right whitespace-nowrap">
        <button type="button" class="p-1.5 rounded-full hover:bg-gray-100 cursor-pointer" aria-label="Edit rule" onClick={() => onEdit(rule)}>
          <Pencil className="h-4 w-4 text-gray-500" />
        </button>
        {!rule.is_global && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button class="p-1.5 rounded-full hover:bg-red-50 cursor-pointer" aria-label="Delete rule">
                <Trash2 className="h-4 w-4 text-red-500" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this rule?</AlertDialogTitle>
                <AlertDialogDescription>
                  {rule.replaces_category_id
                    ? "This removes your customized copy and restores the standard rule."
                    : "This permanently removes the rule. Rules still referenced by deductions can't be deleted — disable or merge them instead."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div class="flex justify-end gap-2 mt-4">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={(() => void onDelete(rule)) as any}>Delete</AlertDialogAction>
              </div>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </td>
    </tr>
  )
}

function Dash() {
  return <span class="text-gray-300">—</span>
}

// Inline tests
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest

  function rule(partial: Partial<CategoryRule>): CategoryRule {
    return {
      id: "1",
      name: "Freight",
      description: "Freight fee",
      source: "unfi",
      retailer: null,
      invoice_regex: null,
      remark_regex: null,
      dispute_email_to: null,
      is_global: true,
      disabled: false,
      replaces_category_id: null,
      matched_count: 0,
      manual_count: 0,
      example_invoice_numbers: [],
      duplicate_count: 1,
      ...partial,
    }
  }

  it("matches_type_filter distinguishes standard, custom, and modified", () => {
    const standard = rule({ is_global: true })
    const custom = rule({ is_global: false })
    const modified = rule({ is_global: false, replaces_category_id: "g1" })
    expect(matches_type_filter(standard, "standard")).toBe(true)
    expect(matches_type_filter(standard, "custom")).toBe(false)
    expect(matches_type_filter(custom, "custom")).toBe(true)
    expect(matches_type_filter(custom, "modified")).toBe(false)
    expect(matches_type_filter(modified, "modified")).toBe(true)
    expect(matches_type_filter(modified, "custom")).toBe(true)
    expect(matches_type_filter(standard, "all")).toBe(true)
  })

  it("group_rules_by_name groups case-insensitively and sorts by usage", () => {
    const groups = group_rules_by_name([
      rule({ id: "1", name: "Freight", matched_count: 1 }),
      rule({ id: "2", name: "freight", matched_count: 2 }),
      rule({ id: "3", name: "Spoilage", matched_count: 10 }),
    ])
    expect(groups.map((g) => g.name)).toEqual(["Spoilage", "Freight"])
    expect(groups[1].rules.length).toBe(2)
    expect(groups[1].matched_count).toBe(3)
  })

  it("rule_pattern_summary describes both patterns", () => {
    expect(rule_pattern_summary(rule({ invoice_regex: "FR\\d+" }))).toBe("Invoice # is exactly “FR” then numbers")
    expect(rule_pattern_summary(rule({ invoice_regex: "FR\\d+", remark_regex: ".*freight.*" }))).toBe(
      "Invoice # is exactly “FR” then numbers, or Description contains “freight”",
    )
    // Amazon requires both patterns to match
    expect(rule_pattern_summary(rule({ source: "amazon", invoice_regex: "\\d+-\\d+", remark_regex: "SPA.*" }))).toContain(" and ")
    expect(rule_pattern_summary(rule({}))).toBe("")
  })
}
